import { useCallback, useMemo } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import type { InnerCircleResponse } from '@/api/types/Subject'
import { statesData, type StateData } from '@/utils/stateData'

interface IndividualInnerCircleFormType {
  avatar?: File | null
  previousAvatar?: string
  firstName: InnerCircleResponse['user']['details']['firstName']
  lastName: InnerCircleResponse['user']['details']['lastName']
  email: InnerCircleResponse['user']['email']
  city?: InnerCircleResponse['user']['details']['city']
  state?: StateData['state']
}

const useIndividualInnerCircleForm = (innerCircle: Partial<InnerCircleResponse> | null) => {
  const defaultValues = useCallback((i: Partial<InnerCircleResponse> | null): IndividualInnerCircleFormType => ({
    avatar: undefined,
    previousAvatar: i?.user?.assets?.PROFILE_PICTURE || '',
    firstName: i?.user?.details?.firstName || '',
    lastName: i?.user?.details?.lastName || '',
    email: i?.user?.email || '',
    city: i?.user?.details?.city || '',
  }), [])

  const validationSchema = useMemo(() => z.object({
    avatar: z.instanceof(File).nullish(),
    firstName: z.string().min(2, 'First name must be at least 2 characters'),
    lastName: z.string().min(2, 'Last name must be at least 2 characters'),
    email: z.email('Invalid email address'),
    city: z.string().optional(),
    state: z.string().refine(
      val => !val || statesData.some(s => s.state === val),
      { message: 'Invalid state' }
    ).optional(),
  }), [])

  const formDefaults = useMemo(() => defaultValues(innerCircle), [innerCircle, defaultValues])

  const form = useForm<IndividualInnerCircleFormType>({
    defaultValues: formDefaults,
    resolver: zodResolver(validationSchema),
  })

  const resetForm = useCallback((i: Partial<InnerCircleResponse> | null) => {
    form.reset(defaultValues(i))
  }, [form, defaultValues])

  return { form, defaultValues, resetForm }
}

export default useIndividualInnerCircleForm
